/** Indonesian formatting helpers. All money is integer rupiah. */

const rupiahFmt = new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0, minimumFractionDigits: 0 });
const numberFmt = new Intl.NumberFormat("id-ID");
const dateFmt = new Intl.DateTimeFormat("id-ID", { day: "numeric", month: "short", year: "numeric" });
const dateTimeFmt = new Intl.DateTimeFormat("id-ID", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
const shortDayFmt = new Intl.DateTimeFormat("id-ID", { day: "numeric", month: "short" });

/** 199000 → "Rp 199.000" (non-breaking space between Rp and the amount). */
export function formatRupiah(value: number | null | undefined): string {
  const n = typeof value === "number" && Number.isFinite(value) ? Math.round(value) : 0;
  // Intl emits "Rp 199.000" with a NBSP already; normalize any whitespace variant to a single NBSP.
  return rupiahFmt.format(n).replace(/^Rp\s*/, "Rp ");
}

/** "Gratis" for 0, otherwise formatRupiah. */
export function formatPrice(value: number | null | undefined, free = "Gratis"): string {
  return !value ? free : formatRupiah(value);
}

export function formatNumber(value: number): string {
  return numberFmt.format(value);
}

/** Digits typed in a rupiah input → integer ("Rp 12.500" → 12500). Returns undefined for empty input. */
export function parseRupiahInput(input: string): number | undefined {
  const digits = input.replace(/[^0-9]/g, "");
  if (!digits) return undefined;
  const n = Number(digits.slice(0, 11));
  return Number.isFinite(n) ? n : undefined;
}

/** Group digits while typing: 12500 → "12.500". */
export function formatRupiahDigits(value: number | undefined): string {
  return value === undefined ? "" : numberFmt.format(value);
}

function toDate(value: string | number | Date): Date {
  return value instanceof Date ? value : new Date(value);
}

/** "28 Sep 2026" */
export function formatDate(value: string | number | Date): string {
  const d = toDate(value);
  return Number.isNaN(d.getTime()) ? "" : dateFmt.format(d);
}

/** "28 Sep 2026, 14.05" */
export function formatDateTime(value: string | number | Date): string {
  const d = toDate(value);
  return Number.isNaN(d.getTime()) ? "" : dateTimeFmt.format(d);
}

/** "28 Sep" */
export function formatShortDate(value: string | number | Date): string {
  const d = toDate(value);
  return Number.isNaN(d.getTime()) ? "" : shortDayFmt.format(d);
}

/**
 * Relative time in Indonesian: "baru saja", "2 menit lalu", "3 jam lalu", "kemarin", "4 hari lalu",
 * then an absolute date. Future times: "dalam 5 menit", "dalam 3 hari".
 */
export function formatRelative(value: string | number | Date, now: number = Date.now()): string {
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return "";
  const diff = Math.round((now - d.getTime()) / 1000);
  const abs = Math.abs(diff);
  const future = diff < 0;
  const unit = (n: number, label: string) => (future ? `dalam ${n} ${label}` : `${n} ${label} lalu`);
  if (abs < 45) return future ? "sebentar lagi" : "baru saja";
  if (abs < 3600) return unit(Math.max(1, Math.round(abs / 60)), "menit");
  if (abs < 86400) return unit(Math.round(abs / 3600), "jam");
  const days = Math.round(abs / 86400);
  if (days === 1) return future ? "besok" : "kemarin";
  if (days < 7) return unit(days, "hari");
  if (days < 30 && future) return unit(days, "hari");
  return formatDate(d);
}

/** Whole days until a date (negative when past). */
export function daysUntil(value: string | number | Date, now: number = Date.now()): number {
  return Math.ceil((toDate(value).getTime() - now) / 86400000);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${numberFmt.format(Math.round(bytes / 1024))} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${numberFmt.format(Math.round((bytes / 1024 / 1024) * 10) / 10)} MB`;
  return `${numberFmt.format(Math.round((bytes / 1024 / 1024 / 1024) * 10) / 10)} GB`;
}

/** Compact number for stats: 1200 → "1,2 rb", 1500000 → "1,5 jt". */
export function formatCompact(n: number): string {
  if (n < 1000) return numberFmt.format(n);
  if (n < 1_000_000) return `${numberFmt.format(Math.round(n / 100) / 10)} rb`;
  return `${numberFmt.format(Math.round(n / 100_000) / 10)} jt`;
}
